import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  readVideoPreferences,
  writeVideoPreferences,
} from "@/utils/videoPreferences";

/**
 * Media-element plumbing shared by every player that renders VideoControlBar.
 *
 * These live apart from `useVideoControls` because SynchronizedVideoPlayer
 * cannot adopt that hook wholesale — it already owns play state, time and
 * volume against the telemetry session clock rather than the media's own
 * currentTime. It can, however, reuse each of these, which depend on nothing
 * but the element itself.
 */

/**
 * Runs `attach(video)` whenever the element held by `videoRef` changes, and
 * detaches the previous wiring. Handles elements that mount late
 * (placeholder-gated players) or are replaced wholesale (players that remount
 * the element per source), where a `[videoRef]`-deps effect would bind once
 * and never follow. The `attach` closure is read fresh from the latest
 * render each time the element changes; identity-unchanged renders are
 * no-ops.
 *
 * The wiring is attached once per element identity: `attach` must not close
 * over render-volatile values it wants to stay current (read them at event
 * time instead — see usePlaybackRate).
 */
export function useVideoElementWiring(
  videoRef: RefObject<HTMLVideoElement>,
  attach: (video: HTMLVideoElement) => () => void,
): void {
  const attachedVideoRef = useRef<HTMLVideoElement | null>(null);
  const detachWiringRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video === attachedVideoRef.current) {
      return;
    }
    detachWiringRef.current?.();
    detachWiringRef.current = null;
    attachedVideoRef.current = video;
    if (!video) {
      return;
    }
    detachWiringRef.current = attach(video);
  });

  // Detach on real unmount; resetting the identity also lets a Strict Mode
  // double mount re-attach to the same element.
  useEffect(
    () => () => {
      detachWiringRef.current?.();
      detachWiringRef.current = null;
      attachedVideoRef.current = null;
    },
    [],
  );
}

/**
 * Reads how far the browser has buffered continuously from `time`. Returns
 * null when `time` sits in a hole, so the scrubber can drop the buffered
 * track rather than draw a misleading one.
 */
export function bufferedEndAt(
  video: HTMLVideoElement,
  time: number,
): number | null {
  const { buffered } = video;
  for (let index = 0; index < buffered.length; index += 1) {
    if (buffered.start(index) <= time && time <= buffered.end(index)) {
      return buffered.end(index);
    }
  }
  return null;
}

/** End of the buffered range under the playhead, tracked as it grows. */
export function useBufferedEnd(
  videoRef: RefObject<HTMLVideoElement>,
): number | null {
  const [bufferedEnd, setBufferedEnd] = useState<number | null>(null);

  useVideoElementWiring(videoRef, (video) => {
    const sync = () => setBufferedEnd(bufferedEndAt(video, video.currentTime));

    video.addEventListener("progress", sync);
    video.addEventListener("timeupdate", sync);
    video.addEventListener("loadedmetadata", sync);
    sync();

    return () => {
      video.removeEventListener("progress", sync);
      video.removeEventListener("timeupdate", sync);
      video.removeEventListener("loadedmetadata", sync);
    };
  });

  return bufferedEnd;
}

/**
 * Fullscreen over the player shell rather than the <video>. Fullscreening the
 * element itself hands rendering to the browser's own fullscreen UI, which no
 * overlay can sit on top of — the control bar would vanish exactly when it is
 * most needed.
 */
export function useFullscreen(
  containerRef: RefObject<HTMLElement>,
  videoRef: RefObject<HTMLVideoElement>,
): { isFullscreen: boolean; toggleFullscreen: () => void } {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Tracked through the event, never set optimistically on toggle: the viewer
  // can leave fullscreen with Esc or the browser chrome, neither of which
  // routes through our button.
  useEffect(() => {
    const handleChange = () =>
      setIsFullscreen(
        Boolean(
          containerRef.current &&
            document.fullscreenElement === containerRef.current,
        ),
      );
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, [containerRef]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (container && document.fullscreenElement === container) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    if (container?.requestFullscreen) {
      void container.requestFullscreen().catch(() => undefined);
      return;
    }
    // iPhone Safari implements fullscreen only on the video element. The
    // native player takes over and the bar is hidden there, which is the
    // accepted degradation rather than having no fullscreen at all.
    const webkitVideo = videoRef.current as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    webkitVideo?.webkitEnterFullscreen?.();
  }, [containerRef, videoRef]);

  return { isFullscreen, toggleFullscreen };
}

export function usePictureInPicture(videoRef: RefObject<HTMLVideoElement>): {
  isSupported: boolean;
  isActive: boolean;
  togglePip: () => void;
} {
  const [isActive, setIsActive] = useState(false);
  const isSupported =
    typeof document !== "undefined" &&
    Boolean(document.pictureInPictureEnabled);

  useVideoElementWiring(videoRef, (video) => {
    const handleEnter = () => setIsActive(true);
    const handleLeave = () => setIsActive(false);

    video.addEventListener("enterpictureinpicture", handleEnter);
    video.addEventListener("leavepictureinpicture", handleLeave);
    // Removing an element in PiP closes it, but the leave event never
    // reaches the detached element — re-derive instead of carrying over.
    setIsActive(document.pictureInPictureElement === video);
    return () => {
      video.removeEventListener("enterpictureinpicture", handleEnter);
      video.removeEventListener("leavepictureinpicture", handleLeave);
    };
  });

  const togglePip = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isSupported) {
      return;
    }
    if (document.pictureInPictureElement === video) {
      void document.exitPictureInPicture().catch(() => undefined);
      return;
    }
    void video.requestPictureInPicture().catch(() => undefined);
  }, [isSupported, videoRef]);

  return { isSupported, isActive, togglePip };
}

/**
 * Playback speed, re-asserted on every source change. Loading a new media
 * resource resets playbackRate to defaultPlaybackRate, so without the
 * reassert a fragment auto-advance would silently drop the viewer's chosen
 * speed back to 1×. With `persist` (the default) the rate is seeded from and
 * written to the viewer's saved preferences; surfaces without a rate control
 * pass false to stay neutral — they play at 1× and never touch the
 * preference.
 */
export function usePlaybackRate(
  videoRef: RefObject<HTMLVideoElement>,
  options: { persist?: boolean } = {},
): {
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
} {
  const { persist = true } = options;
  const [playbackRate, setRate] = useState(() =>
    persist ? readVideoPreferences().playbackRate : 1,
  );

  const applyRate = useCallback(
    (video: HTMLVideoElement, rate: number) => {
      video.defaultPlaybackRate = rate;
      video.playbackRate = rate;
    },
    [],
  );

  useVideoElementWiring(videoRef, (video) => {
    // The wiring stays attached across renders, so the reassert must not
    // close over the attach-time rate: read the persisted preference at
    // event time instead — setPlaybackRate keeps it in step with the state,
    // mirroring how useVideoControls re-reads volume/mute on loadedmetadata.
    // Persistence-off surfaces stay neutral at 1×.
    const reassert = () =>
      applyRate(video, persist ? readVideoPreferences().playbackRate : 1);

    video.addEventListener("loadedmetadata", reassert);
    // The element may already be past loadedmetadata when wiring attaches
    // (Strict Mode remount, or a source attached by a sibling effect first).
    reassert();

    return () => video.removeEventListener("loadedmetadata", reassert);
  });

  const setPlaybackRate = useCallback(
    (rate: number) => {
      const video = videoRef.current;
      if (video) {
        applyRate(video, rate);
      }
      setRate(rate);
      if (persist) {
        writeVideoPreferences({ playbackRate: rate });
      }
    },
    [applyRate, persist, videoRef],
  );

  return { playbackRate, setPlaybackRate };
}
